import classNames from 'classnames';

import lang from '../../../lib/language';
import { Referral } from '../../../lib/models/crm/types';
import { formatCurrency } from '../../../lib/util/DataHelpers';

import { GlobalClassesEnum } from '../../../assets/scss/globalClasses/globalClasses.constants';
import { formatReferralDate, groupReferralsByRegistration } from '../../../containers/Referrals/helpers';
import { useIsMobile } from '../../../hooks';
import { getReferralStatusBadge } from '../helpers';
import ReferralsEmptyState from '../ReferralsEmptyState/ReferralsEmptyState';
import ReferralStatusBadge from '../ReferralStatusBadge/ReferralStatusBadge';

import styles from './ReferralsTabContent.module.scss';

type Props = {
  referrals: Referral[];
};

const secondaryClasses = classNames(
  GlobalClassesEnum.CommonBody2Text,
  GlobalClassesEnum.CommonTextSecondary,
);
const headerCellClasses = classNames(
  GlobalClassesEnum.CommonCaption,
  GlobalClassesEnum.CommonTextSecondary,
  styles.headerCell,
);
const cellClasses = classNames(GlobalClassesEnum.CommonBody1Text, styles.cell);

const ReferralsTabContent = ({ referrals }: Props) => {
  const { isMobile, isTablet } = useIsMobile();

  if (!referrals.length) {
    return (
      <ReferralsEmptyState
        title={lang.referralsEmptyReferralsTitle()}
        description={lang.referralsEmptyReferralsDescription()}
      />
    );
  }

  if (isMobile || isTablet) {
    return (
      <div className={styles.groups}>
        {groupReferralsByRegistration(referrals).map((group) => (
          <div className={styles.group} key={group.registeredAt}>
            <div className={secondaryClasses}>
              {lang.referralsGroupRegistered(formatReferralDate(group.registeredAt))}
            </div>
            <div className={styles.groupCard}>
              {group.items.map((referral) => {
                const badge = getReferralStatusBadge(referral.status);

                return (
                  <div className={styles.cardRow} key={referral.id}>
                    <div className={styles.cardUser}>
                      <div className={GlobalClassesEnum.CommonBody2TextBold}>{referral.maskedEmail}</div>
                      <ReferralStatusBadge label={badge.label} variant={badge.variant} compact />
                    </div>
                    <div className={GlobalClassesEnum.CommonBody2TextBold}>
                      {formatCurrency(referral.commission)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.table}>
      <div className={styles.headerRow}>
        <div className={headerCellClasses}>{lang.referralsColumnUser()}</div>
        <div className={headerCellClasses}>{lang.referralsColumnRegistered()}</div>
        <div className={headerCellClasses}>{lang.referralsColumnCommission()}</div>
        <div className={headerCellClasses}>{lang.referralsColumnStatus()}</div>
      </div>
      {referrals.map((referral) => {
        const badge = getReferralStatusBadge(referral.status);

        return (
          <div className={styles.row} key={referral.id}>
            <div className={cellClasses}>{referral.maskedEmail}</div>
            <div className={cellClasses}>{formatReferralDate(referral.registeredAt)}</div>
            <div className={cellClasses}>{formatCurrency(referral.commission)}</div>
            <div className={cellClasses}>
              <ReferralStatusBadge label={badge.label} variant={badge.variant} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ReferralsTabContent;
